import { Select } from "@light-technology/components";
import { RefreshCcw02 } from "@untitledui/icons";
import type { Key, ReactNode } from "react";
import { useController, type Control } from "react-hook-form";
import type { PlanResponse } from "../../state/api.types";
import type { FormValues } from "./common";
import { IneligibleReason, type RenewalPlanOption } from "./renewalPlan";

const INELIGIBILITY_HINTS = new Map<
  IneligibleReason,
  (plan: RenewalPlanOption) => ReactNode
>([
  [IneligibleReason.Inactive, () => "Selected renewal plan is not active."],
  [
    IneligibleReason.CircularDependency,
    (p) => (
      <>
        Selecting this plan would create a renewal loop between plans. First,
        update the renewal plan for the {p.name} Plan.
      </>
    ),
  ],
]);

type EditRenewalPlanProps = {
  plan: PlanResponse;
  renewalPlanOptions: RenewalPlanOption[];
  control: Control<FormValues>;
};

export const EditRenewalPlan = ({
  plan,
  renewalPlanOptions,
  control,
}: EditRenewalPlanProps) => {
  const { field } = useController({
    control,
    name: "renewalPlanUuid",
    rules: {
      validate: (uuid) =>
        !renewalPlanOptions.find((p) => p.uuid === uuid)?.ineligibleReason,
    },
  });
  const selectedItem =
    renewalPlanOptions.find((p) => p.uuid === field.value) ??
    renewalPlanOptions[0];
  const errorHint = selectedItem.ineligibleReason
    ? INELIGIBILITY_HINTS.get(selectedItem.ineligibleReason)?.(selectedItem)
    : undefined;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <div className="flex w-full flex-row items-center gap-md">
          <RefreshCcw02 />
          <div className="w-full text-lg font-semibold">
            Default renewal plan
          </div>
        </div>
        <div className="text-sm text-tertiary">
          Offer sent to customers near the end of their contract.
        </div>
      </div>
      <div className="flex flex-col gap-sm">
        <Select
          items={renewalPlanOptions
            .filter((p) => p.ineligibleReason !== IneligibleReason.Inactive)
            .map((p) => ({
              ...p,
              id: p.uuid ?? plan.uuid,
              label: p.name ?? "",
            }))}
          value={selectedItem.uuid ?? plan.uuid}
          className={(state) =>
            state.isInvalid ? "[&_button]:ring-2 [&_button]:ring-error" : ""
          }
          onChange={(value: Key | null) => {
            const item = renewalPlanOptions.find(
              (p) => (p.uuid ?? plan.uuid) === value,
            );
            if (item) field.onChange(item.uuid);
          }}
          aria-label="Select renewal plan"
          isInvalid={!!errorHint}
        >
          {(item) => (
            <Select.Item id={item.id}>
              {
                renewalPlanOptions.find(
                  (p) => (p.uuid ?? plan.uuid) === item.id,
                )?.name
              }
            </Select.Item>
          )}
        </Select>
        {errorHint && (
          <div className="text-sm text-error-primary">{errorHint}</div>
        )}
      </div>
    </div>
  );
};
